import React, { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HistorialFilaDrawer from "../../../components/HistorialFilaDrawer";
import PanelFlotante from "../../../components/PanelFlotante";
import BotonVolver from "../../../components/BotonVolver";

// ─────────────────────────────────────────────
// Filtro de columna tipo Excel: lista de valores únicos con checkboxes + buscador
// ─────────────────────────────────────────────
const ColumnFilterPopover = ({ opciones, seleccionados, onChange, align = "center" }) => {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [temp, setTemp] = useState(seleccionados);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(seleccionados); }, [abierto, seleccionados]);
  const cerrar = useCallback(() => setAbierto(false), []);

  const etiqueta = (o) => (o === "" ? "(vacío)" : String(o));
  const filtradas = busqueda.trim()
    ? opciones.filter((o) => etiqueta(o).toLowerCase().includes(busqueda.trim().toLowerCase()))
    : opciones;
  const toggle = (v) => setTemp((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  const activo = seleccionados.length > 0;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 6 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar"
        style={{
          border: "none", background: activo ? "#1e3a5f" : "rgba(255,255,255,0.25)",
          color: "#fff", borderRadius: 8, cursor: "pointer",
          fontSize: 13, padding: "4px 9px", lineHeight: 1.4, fontWeight: 800,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 14, boxShadow: "0 18px 40px rgba(15,23,42,0.28)",
          width: 260, padding: 14, textAlign: "left", color: "#334155", textTransform: "none", fontWeight: 400,
        }}>
          <input
            value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="🔍 Buscar…"
            style={{ width: "100%", padding: "9px 10px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 8, boxSizing: "border-box", outline: "none" }}
          />
          <div style={{ display: "flex", gap: 12, marginBottom: 8, fontSize: 12.5 }}>
            <span onClick={() => setTemp(filtradas)} style={{ color: "#3498db", cursor: "pointer", fontWeight: 700 }}>Marcar todos</span>
            <span onClick={() => setTemp([])} style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 700 }}>Desmarcar</span>
          </div>
          <div style={{ maxHeight: 230, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
            {filtradas.length === 0 && <div style={{ fontSize: 13, color: "#94a3b8", padding: 4 }}>Sin valores</div>}
            {filtradas.map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", padding: "4px 2px" }}>
                <input type="checkbox" checked={temp.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{etiqueta(o)}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={() => { onChange([]); setAbierto(false); }} style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: "none", background: "#3498db", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

const AdminGestionAlumnos = () => {
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importando, setImportando] = useState(false);
  const [error, setError] = useState("");
  const [drawer, setDrawer] = useState(null); // { filaId, titulo } | null

  const [filtros, setFiltros] = useState({
    id: [],
    nombre: [],
    abreviatura_piso: [],
    habitacion: [],
    duracion: [],
    pais: [],
    a_r: [],
  });
  const [filtroEstado, setFiltroEstado] = useState("todos"); // "todos" | "alta" | "baja"
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAlumnos = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/alumnos/`);
        const ordenados = (res.data || []).sort((a, b) =>
          (a.nombre || "").localeCompare(b.nombre || "")
        );
        setAlumnos(ordenados);
        setError("");
      } catch (err) {
        setError("❌ Error al cargar alumnos.");
      } finally {
        setLoading(false);
      }
    };
    fetchAlumnos();
  }, []);

  const opcionesUnicas = (campo) => {
    const vistos = new Set();
    const opciones = [];
    let hayVacios = false;
    alumnos.forEach((a) => {
      const v = a[campo];
      if (v === null || v === undefined || v === "") { hayVacios = true; return; }
      if (!vistos.has(v)) { vistos.add(v); opciones.push(v); }
    });
    opciones.sort();
    if (hayVacios) opciones.push("");
    return opciones;
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Dar de baja a este alumno?")) return;
    try {
      await axios.delete(`/api/alumnos/${id}`);
      setAlumnos((prev) =>
        prev.map((a) =>
          a.id_alumno === id ? { ...a, activo: 0, fecha_baja: new Date().toISOString() } : a
        )
      );
    } catch {
      alert("❌ No se pudo dar de baja al alumno.");
    }
  };

  const handleImportar = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.xlsx,.xls";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append("file", file);
      try {
        setImportando(true);
        const res = await axios.post(`/api/alumnos/importar`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const desc = Array.isArray(res.data?.filas_descartadas) ? res.data.filas_descartadas : [];
        let msg = `✅ Importados ${res.data?.total_importados ?? 0} alumnos.`;
        msg += `\n🆕 Altas: ${res.data?.altas ?? 0} · ✏️ Modificados: ${res.data?.modificaciones ?? 0} · 🚫 Bajas: ${res.data?.bajas_detectadas ?? 0}`;
        if (desc.length) msg += `\nℹ️ Descartados: ${desc.length} (sin id_piso o piso inexistente).`;
        alert(msg);
        window.location.reload();
      } catch (err) {
        const dataErr = err.response?.data;
        let msg = dataErr?.error || "❌ Error al importar.";
        if (Array.isArray(dataErr?.filas_error) && dataErr.filas_error.length) {
          msg += "\n\nErrores:\n" + dataErr.filas_error.slice(0, 10).map(e => `  Fila ${e.fila}: ${e.motivo}`).join("\n");
        }
        alert(msg);
      } finally {
        setImportando(false);
      }
    };
    input.click();
  };

  const alumnosFiltrados = alumnos.filter((a) => {
    if (filtroEstado === "alta" && a.activo === 0) return false;
    if (filtroEstado === "baja" && a.activo !== 0) return false;
    if (filtros.id.length && !filtros.id.includes(a.id_alumno)) return false;
    if (filtros.nombre.length && !filtros.nombre.includes(a.nombre ?? "")) return false;
    if (filtros.abreviatura_piso.length && !filtros.abreviatura_piso.includes(a.abreviatura_piso ?? "")) return false;
    if (filtros.habitacion.length && !filtros.habitacion.includes(a.habitacion ?? "")) return false;
    if (filtros.duracion.length && !filtros.duracion.includes(a.duracion ?? "")) return false;
    if (filtros.pais.length && !filtros.pais.includes(a.pais ?? "")) return false;
    if (filtros.a_r.length && !filtros.a_r.includes(a.a_r ?? "")) return false;
    return true;
  });

  const hayFiltrosActivos = filtroEstado !== "todos" || filtros.id.length > 0 || filtros.nombre.length > 0 || filtros.abreviatura_piso.length > 0
    || filtros.habitacion.length > 0 || filtros.duracion.length > 0 || filtros.pais.length > 0 || filtros.a_r.length > 0;
  const limpiarFiltros = () => {
    setFiltros({ id: [], nombre: [], abreviatura_piso: [], habitacion: [], duracion: [], pais: [], a_r: [] });
    setFiltroEstado("todos");
  };

  return (
    <div style={styles.container}>
      <BotonVolver fallback="/admin" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={styles.title}>Gestión de Alumnos</h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={styles.estadoGroup}>
            {[
              { key: "todos", label: "Todos" },
              { key: "alta", label: "Altas" },
              { key: "baja", label: "Bajas" },
            ].map((op) => (
              <button
                key={op.key}
                onClick={() => setFiltroEstado(op.key)}
                style={filtroEstado === op.key ? styles.estadoBtnActivo : styles.estadoBtn}
              >
                {op.label}
              </button>
            ))}
          </div>
          {hayFiltrosActivos && (
            <button style={styles.clearFilters} onClick={limpiarFiltros}>
              ❌ Quitar filtros
            </button>
          )}
          <button
            style={styles.historial}
            onClick={() => navigate("/admin/alumnos/historial")}
            onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(94, 53, 177, 0.45)"; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 10px rgba(94, 53, 177, 0.35)"; }}
          >
            📜 Historial
          </button>
          <button style={styles.import} onClick={handleImportar} disabled={importando}>
            {importando ? "⏳ Importando..." : "📥 Importar CSV/Excel"}
          </button>
        </div>
      </div>

      {!loading && !importando && !error && (
        <div style={styles.contadorFiltro}>
          🎓 {hayFiltrosActivos ? (
            <>Mostrando <strong>{alumnosFiltrados.length}</strong> de <strong>{alumnos.length}</strong> alumnos</>
          ) : (
            <><strong>{alumnos.length}</strong> alumnos en total</>
          )}
        </div>
      )}

      {loading ? (
        <p>Cargando alumnos...</p>
      ) : importando ? (
        <p>Importando alumnos...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : alumnosFiltrados.length === 0 ? (
        <p style={{ marginTop: 30, textAlign: "center" }}>No se encontraron alumnos ({alumnos.length} en total, filtros muy restrictivos).</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>ID</span><ColumnFilterPopover opciones={opcionesUnicas("id_alumno")} seleccionados={filtros.id} onChange={(v) => setFiltros((f) => ({ ...f, id: v }))} align="left" /></div>
              </th>
              <th style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Nombre</span><ColumnFilterPopover opciones={opcionesUnicas("nombre")} seleccionados={filtros.nombre} onChange={(v) => setFiltros((f) => ({ ...f, nombre: v }))} /></div>
              </th>
              <th style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Piso</span><ColumnFilterPopover opciones={opcionesUnicas("abreviatura_piso")} seleccionados={filtros.abreviatura_piso} onChange={(v) => setFiltros((f) => ({ ...f, abreviatura_piso: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Hab.</span><ColumnFilterPopover opciones={opcionesUnicas("habitacion")} seleccionados={filtros.habitacion} onChange={(v) => setFiltros((f) => ({ ...f, habitacion: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Duración</span><ColumnFilterPopover opciones={opcionesUnicas("duracion")} seleccionados={filtros.duracion} onChange={(v) => setFiltros((f) => ({ ...f, duracion: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>País</span><ColumnFilterPopover opciones={opcionesUnicas("pais")} seleccionados={filtros.pais} onChange={(v) => setFiltros((f) => ({ ...f, pais: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>A/R</span><ColumnFilterPopover opciones={opcionesUnicas("a_r")} seleccionados={filtros.a_r} onChange={(v) => setFiltros((f) => ({ ...f, a_r: v }))} /></div>
              </th>
              <th style={styles.headerCell}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {alumnosFiltrados.map((a) => (
              <tr key={a.id_alumno} style={a.activo === 0 ? styles.rowBaja : styles.row}>
                <td className="ocultar-en-movil" style={styles.cell}>{a.id_alumno}</td>
                <td style={styles.cell}>
                  <strong>{a.nombre}</strong>
                  {a.activo === 0 && <span style={styles.badgeBaja}>BAJA</span>}
                </td>
                <td style={styles.cell}>{a.abreviatura_piso || a.id_piso}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{a.habitacion || ""}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{a.duracion || ""}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{a.pais || ""}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{a.a_r || ""}</td>
                <td style={styles.cell}>
                  <button
                    style={styles.historialFila}
                    onClick={() => setDrawer({ filaId: a.id_alumno, titulo: a.nombre })}
                  >
                    🕓 Historial
                  </button>
                  <button style={styles.edit} onClick={() => navigate(`/alumnos/editar/${a.id_alumno}`)}>
                    ✏️ Editar
                  </button>
                  {a.activo !== 0 && (
                    <button style={styles.delete} onClick={() => handleEliminar(a.id_alumno)}>
                      🗑 Dar de baja
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button style={styles.add} onClick={() => navigate("/alumnos/nuevo")}>
        ➕ Añadir Alumno
      </button>

      <HistorialFilaDrawer
        abierto={!!drawer}
        endpoint="/api/alumnos/historial"
        filaId={drawer?.filaId}
        titulo={drawer?.titulo}
        onCerrar={() => setDrawer(null)}
      />
    </div>
  );
};

const styles = {
  clearFilters: {
    padding: "12px 18px",
    backgroundColor: "#95a5a6",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "15px",
    whiteSpace: "nowrap",
  },
  container: { padding: "30px", fontFamily: "Segoe UI, sans-serif", position: "relative" },
  title: { fontSize: "26px", marginBottom: "20px", fontWeight: "700", textAlign: "center", color: "#2c3e50" },
  hdrFlex: { display: "flex", alignItems: "center", justifyContent: "center", gap: 4 },
  table: { width: "100%", borderCollapse: "collapse", boxShadow: "0 2px 8px rgb(0 0 0 / 0.1)", borderRadius: "10px", overflow: "hidden" },
  headerCell: { padding: "16px 12px", backgroundColor: "#3498db", color: "white", fontWeight: "700", fontSize: "15px", textAlign: "center" },
  row: { backgroundColor: "#fafafa", borderBottom: "1px solid #e1e1e1" },
  rowBaja: { backgroundColor: "#f2f2f2", borderBottom: "1px solid #e1e1e1", opacity: 0.65 },
  contadorFiltro: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    margin: "10px 0 4px",
    padding: "6px 14px",
    borderRadius: "999px",
    backgroundColor: "#eaf2fb",
    color: "#2c5c8f",
    fontSize: "13.5px",
    fontWeight: "500",
  },
  estadoGroup: {
    display: "flex",
    gap: "4px",
    backgroundColor: "#eef1f4",
    borderRadius: "8px",
    padding: "4px",
  },
  estadoBtn: {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "transparent",
    color: "#5a6472",
    fontWeight: "700",
    fontSize: "13.5px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  estadoBtnActivo: {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#3498db",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13.5px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  badgeBaja: {
    display: "inline-block",
    marginLeft: "8px",
    padding: "2px 8px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#fff",
    backgroundColor: "#e74c3c",
    borderRadius: "10px",
    verticalAlign: "middle",
  },
  cell: { padding: "12px", textAlign: "center", fontSize: "14px", color: "#34495e" },
  edit: { padding: "7px 14px", marginRight: "8px", backgroundColor: "#f39c12", border: "none", borderRadius: "6px", cursor: "pointer", color: "#fff", fontWeight: "600" },
  delete: { padding: "7px 14px", backgroundColor: "#e74c3c", border: "none", borderRadius: "6px", cursor: "pointer", color: "#fff", fontWeight: "600" },
  historialFila: { padding: "7px 14px", marginRight: "8px", backgroundColor: "#5e35b1", border: "none", borderRadius: "6px", cursor: "pointer", color: "#fff", fontWeight: "600" },
  historial: { padding: "14px 26px", background: "linear-gradient(135deg, #7e57c2, #5e35b1)", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "700", fontSize: "16px", whiteSpace: "nowrap", boxShadow: "0 4px 10px rgba(94, 53, 177, 0.35)", transition: "transform 0.15s ease, box-shadow 0.15s ease" },
  import: { padding: "14px 26px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "700", fontSize: "16px", marginLeft: "12px", whiteSpace: "nowrap", boxShadow: "0 4px 8px rgba(39, 174, 96, 0.4)" },
  add: { position: "fixed", bottom: "30px", right: "30px", padding: "16px 24px", fontSize: "18px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "50px", cursor: "pointer", boxShadow: "0 4px 12px rgb(39 174 96 / 0.5)" },
};

export default AdminGestionAlumnos;
